import {
    BUDGET_SPREADSHEET_COLUMNS,
    BudgetExportFlatRow,
    BudgetSpreadsheetColumnKey,
    BudgetSpreadsheetUtil,
} from "../../../student-engagement/budget/budget.spreadsheet.util";
import { CellData, RowData, SheetEntry } from "../../../s25-spreadsheet/types/sheet.js.types.expanded";
import { ImportUtil } from "../import.util";
import { S25Util } from "../../../../util/s25-util";
import { ImportBudget } from "../../import.budget";
import { Budget } from "../../../student-engagement/budget/Budget";

type BudgetImportColumn = BudgetSpreadsheetColumnKey;

/** Short / snake_case aliases only — display headers come from BUDGET_SPREADSHEET_COLUMNS. */
const HEADER_ALIASES: Array<[string, BudgetImportColumn]> = [
    ["organizationid", "organizationId"],
    ["orgid", "organizationId"],
    ["acctid", "organizationId"],
    ["organizationname", "organizationName"],
    ["orgname", "organizationName"],
    ["acctname", "organizationName"],
    ["fiscalyear", "fiscalYearName"],
    ["fy", "fiscalYearName"],
    ["budgetname", "budgetName"],
    ["orgbudgetname", "budgetName"],
    ["name", "budgetName"],
    ["orgbudgetdescription", "orgBudgetDescription"],
    ["budgetdescription", "orgBudgetDescription"],
    ["totalallocated", "totalAllocated"],
    ["orgbudgettotalallocated", "totalAllocated"],
    ["budgetplanname", "planName"],
    ["planname", "planName"],
    ["budgetplantype", "planType"],
    ["plantype", "planType"],
    ["budgetplandescription", "planDescription"],
    ["plandescription", "planDescription"],
    ["planallocatedamount", "planAllocatedAmount"],
    ["allocatedamount", "planAllocatedAmount"],
    ["budgetplanstartdate", "planStartDate"],
    ["planstartdate", "planStartDate"],
    ["planstart", "planStartDate"],
    ["budgetplanenddate", "planEndDate"],
    ["planenddate", "planEndDate"],
    ["planend", "planEndDate"],
    ["lineitemname", "lineItemName"],
    ["lineitemcategory", "lineCategoryName"],
    ["categoryname", "lineCategoryName"],
    ["lineitemdescription", "lineItemDescription"],
    ["lineitemcomments", "lineItemComments"],
    ["comments", "lineItemComments"],
    ["lineitemplannedamount", "linePlannedAmount"],
    ["plannedamount", "linePlannedAmount"],
];

type ParsedRowSlice = {
    organizationId?: number;
    organizationName?: string;
    fiscalYearId?: number;
    fiscalYearName?: string;
    budgetName?: string;
    orgBudgetDescription?: string;
    totalAllocated?: number;
    planName?: string;
    planType?: string;
    planDescription?: string;
    planAllocatedAmount?: number;
    planStartDate?: string;
    planEndDate?: string;
    lineItemName?: string;
    lineCategoryId?: number;
    lineCategoryName?: string;
    lineItemDescription?: string;
    lineItemComments?: string;
    linePlannedAmount?: number;
};

export class BudgetImportSpreadsheetUtil {
    public static rowsToBudgetImport(rows: RowData[], errorSet: Set<string>): ImportBudget.RequestDto {
        const budgets: ImportBudget.OrgBudget[] = [];
        const cellNumToColumn = new Map<number, BudgetImportColumn>();

        if (!rows?.length) {
            return { budgets: [] };
        }

        let currentOrgBudgetKey: string | undefined;
        let currentPlanKey: string | undefined;
        const seenLineKeys = new Set<string>();

        for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
            const row = rows[rowIndex];
            const cells = row?.cells;
            if (!row || !cells?.length) {
                continue;
            }

            if (rowIndex === 0) {
                for (let columnIndex = 0; columnIndex < cells.length; columnIndex++) {
                    const headerText = cells[columnIndex]?.text?.trim();
                    if (!headerText) {
                        continue;
                    }
                    const column = this.resolveHeader(headerText);
                    if (column) {
                        cellNumToColumn.set(columnIndex, column);
                    }
                }
                continue;
            }

            const slice = this.cellsToSlice(cells, cellNumToColumn, rowIndex + 1, errorSet);
            if (!this.sliceHasData(slice)) {
                continue;
            }

            const orgBudgetKey = this.resolveOrgBudgetKey(slice, currentOrgBudgetKey, rowIndex + 1, errorSet);
            if (!orgBudgetKey) {
                continue;
            }
            currentOrgBudgetKey = orgBudgetKey;

            const orgBudget = this.findOrCreateOrgBudget(budgets, orgBudgetKey, slice, rowIndex + 1, errorSet);
            if (!orgBudget) {
                continue;
            }

            const hasPlanData = !!(
                slice.planName?.trim() ||
                slice.planType ||
                slice.planDescription?.trim() ||
                slice.planAllocatedAmount != null ||
                slice.planStartDate ||
                slice.planEndDate
            );
            const hasLineData = !!(
                slice.lineItemName?.trim() ||
                slice.lineCategoryId != null ||
                slice.lineCategoryName?.trim() ||
                slice.lineItemDescription?.trim() ||
                slice.lineItemComments?.trim() ||
                slice.linePlannedAmount != null
            );

            if (hasPlanData) {
                const planKey = `${orgBudgetKey}\0${slice.planName?.trim().toLowerCase() ?? ""}`;
                if (slice.planName?.trim()) {
                    currentPlanKey = planKey;
                } else {
                    errorSet.add(`Row ${rowIndex + 1}: Budget plan name is required when plan columns are present`);
                    continue;
                }

                const plan = this.findOrCreatePlan(orgBudget, slice);
                if (!plan) {
                    continue;
                }

                if (hasLineData) {
                    this.addLineItem(plan, slice, `${planKey}`, seenLineKeys, rowIndex + 1, errorSet);
                }
            } else if (hasLineData) {
                if (!currentPlanKey || !currentPlanKey.startsWith(`${orgBudgetKey}\0`)) {
                    errorSet.add(
                        `Row ${rowIndex + 1}: Line item row requires a budget plan name on this row or a preceding row`,
                    );
                    continue;
                }
                const planName = currentPlanKey.substring(orgBudgetKey.length + 1);
                const plan = orgBudget.budgetPlans?.find((entry) => entry.name?.trim().toLowerCase() === planName);
                if (!plan) {
                    errorSet.add(`Row ${rowIndex + 1}: Could not find budget plan "${planName}" for line item`);
                    continue;
                }
                this.addLineItem(plan, slice, currentPlanKey, seenLineKeys, rowIndex + 1, errorSet);
            }
        }

        this.validateRequiredColumns(cellNumToColumn, errorSet);
        this.validateOrgBudgets(budgets, errorSet);

        return { budgets };
    }

    public static buildImportTemplateRows(): SheetEntry[] {
        const sampleExport: Budget.OrgBudgetExport = {
            organizationId: 1001,
            organizationName: "Sample Organization",
            orgBudgets: [
                {
                    budgetId: 1,
                    organizationId: 1001,
                    organizationName: "Sample Organization",
                    fiscalYearId: 26,
                    fiscalYear: { fiscalYearId: 26, name: "FY 2026", startDate: "", endDate: "" },
                    name: "Operating Budget",
                    description: "Annual operating pool",
                    amount: 0,
                    currency: "USD",
                    createUser: "",
                    createDate: "",
                    lastModUser: "",
                    lastModDate: "",
                    startDate: "",
                    endDate: "",
                    totalAllocated: 50000,
                    totalSpent: 1200,
                    totalCommitted: 800,
                    remainingBalance: 48000,
                    budgetPlans: [
                        {
                            budgetPlanId: 5,
                            budgetId: 1,
                            name: "Events Plan",
                            planType: "event",
                            description: "Campus events",
                            amount: 0,
                            currency: "USD",
                            createUser: "",
                            createDate: "",
                            lastModUser: "",
                            lastModDate: "",
                            startDate: "2026-07-01T00:00:00",
                            endDate: "2027-06-30T23:59:59",
                            allocatedAmount: 20000,
                            spentAmount: 0,
                            committedAmount: 0,
                            workflowStatus: "COMPLETED",
                            budgetLineItems: [
                                {
                                    lineItemId: 9,
                                    budgetId: 1,
                                    budgetPlanId: 5,
                                    name: "Guest speaker",
                                    categoryId: 11,
                                    categoryName: "Speakers",
                                    description: "Keynote honorarium",
                                    comments: "Confirm invoice",
                                    amount: 0,
                                    currency: "USD",
                                    createUser: "",
                                    createDate: "",
                                    lastModUser: "",
                                    lastModDate: "",
                                    plannedAmount: 1500,
                                    actualAmount: 0,
                                    committedAmount: 0,
                                },
                                {
                                    lineItemId: 10,
                                    budgetId: 1,
                                    budgetPlanId: 5,
                                    name: "Refreshments",
                                    categoryId: 12,
                                    categoryName: "Food",
                                    description: "",
                                    comments: "",
                                    amount: 0,
                                    currency: "USD",
                                    createUser: "",
                                    createDate: "",
                                    lastModUser: "",
                                    lastModDate: "",
                                    plannedAmount: 800,
                                    actualAmount: 0,
                                    committedAmount: 0,
                                },
                            ],
                        },
                    ],
                },
            ],
        };

        const flatRows = BudgetSpreadsheetUtil.flattenExport(sampleExport);
        return BudgetSpreadsheetUtil.flatRowsToSheetEntries(flatRows);
    }

    /** Build RowData (header + cells) from export flat rows for round-trip tests. */
    public static flatRowsToRowData(flatRows: BudgetExportFlatRow[]): RowData[] {
        const headerRow = {
            cells: BUDGET_SPREADSHEET_COLUMNS.map((column) => ({ text: column.header })),
        } as unknown as RowData;
        const dataRows = flatRows.map(
            (flatRow) =>
                ({
                    cells: BUDGET_SPREADSHEET_COLUMNS.map((column) => {
                        const cellValue = flatRow[column.key];
                        return { text: cellValue === undefined || cellValue === null ? "" : String(cellValue) };
                    }),
                }) as unknown as RowData,
        );
        return [headerRow, ...dataRows];
    }

    private static resolveHeader(rawHeader: string): BudgetImportColumn | undefined {
        const normalized = ImportUtil.normalize(rawHeader);
        for (const column of BUDGET_SPREADSHEET_COLUMNS) {
            if (ImportUtil.normalize(column.header) !== normalized) {
                continue;
            }
            if (column.role === "readonly") {
                return undefined;
            }
            return column.key;
        }
        for (const [alias, column] of HEADER_ALIASES) {
            if (normalized === alias || normalized === ImportUtil.normalize(alias)) {
                return column;
            }
        }
        return undefined;
    }

    private static cellsToSlice(
        cells: CellData[],
        cellNumToColumn: Map<number, BudgetImportColumn>,
        rowNumber: number,
        errorSet: Set<string>,
    ): ParsedRowSlice {
        const slice: ParsedRowSlice = {};
        for (let columnIndex = 0; columnIndex < cells.length; columnIndex++) {
            const column = cellNumToColumn.get(columnIndex);
            const rawText = cells[columnIndex]?.text?.trim();
            if (!column || !rawText) {
                continue;
            }
            switch (column) {
                case "organizationId": {
                    slice.organizationId = S25Util.parseInt(rawText);
                    break;
                }
                case "organizationName": {
                    slice.organizationName = rawText.replace(/^\$NAME-/, "");
                    break;
                }
                case "fiscalYearName": {
                    slice.fiscalYearName = rawText.replace(/^\$NAME-/, "");
                    break;
                }
                case "budgetName": {
                    slice.budgetName = rawText.replace(/^\$NAME-/, "");
                    break;
                }
                case "orgBudgetDescription": {
                    slice.orgBudgetDescription = rawText;
                    break;
                }
                case "totalAllocated": {
                    slice.totalAllocated = S25Util.parseFloat(rawText);
                    break;
                }
                case "planName": {
                    slice.planName = rawText.replace(/^\$NAME-/, "");
                    break;
                }
                case "planType": {
                    slice.planType = rawText;
                    break;
                }
                case "planDescription": {
                    slice.planDescription = rawText;
                    break;
                }
                case "planAllocatedAmount": {
                    slice.planAllocatedAmount = S25Util.parseFloat(rawText);
                    break;
                }
                case "planStartDate": {
                    const isoDateTime = BudgetImportSpreadsheetUtil.parsePlanDateCell(
                        rawText,
                        rowNumber,
                        errorSet,
                        "plan start date",
                    );
                    if (isoDateTime) {
                        slice.planStartDate = isoDateTime;
                    }
                    break;
                }
                case "planEndDate": {
                    const isoDateTime = BudgetImportSpreadsheetUtil.parsePlanDateCell(
                        rawText,
                        rowNumber,
                        errorSet,
                        "plan end date",
                    );
                    if (isoDateTime) {
                        slice.planEndDate = isoDateTime;
                    }
                    break;
                }
                case "lineItemName": {
                    slice.lineItemName = rawText.replace(/^\$NAME-/, "");
                    break;
                }
                case "lineCategoryName": {
                    slice.lineCategoryName = rawText.replace(/^\$NAME-/, "");
                    break;
                }
                case "lineItemDescription": {
                    slice.lineItemDescription = rawText;
                    break;
                }
                case "lineItemComments": {
                    slice.lineItemComments = rawText;
                    break;
                }
                case "linePlannedAmount": {
                    slice.linePlannedAmount = S25Util.parseFloat(rawText);
                    break;
                }
                default:
                    break;
            }
        }
        return slice;
    }

    private static sliceHasData(slice: ParsedRowSlice): boolean {
        return Object.values(slice).some((value) => value != null && `${value}`.trim() !== "");
    }

    private static orgBudgetIdentityKey(slice: ParsedRowSlice): string | undefined {
        const orgPart =
            slice.organizationId != null
                ? `id:${slice.organizationId}`
                : slice.organizationName?.trim()
                  ? `name:${slice.organizationName.trim().toLowerCase()}`
                  : undefined;
        const fyPart =
            slice.fiscalYearId != null
                ? `fy:${slice.fiscalYearId}`
                : slice.fiscalYearName?.trim()
                  ? `fyname:${slice.fiscalYearName.trim().toLowerCase()}`
                  : undefined;
        if (!orgPart || !fyPart) {
            return undefined;
        }
        return `${orgPart}|${fyPart}`;
    }

    private static resolveOrgBudgetKey(
        slice: ParsedRowSlice,
        currentOrgBudgetKey: string | undefined,
        rowNumber: number,
        errorSet: Set<string>,
    ): string | undefined {
        const key = this.orgBudgetIdentityKey(slice);
        if (key) {
            return key;
        }
        if (currentOrgBudgetKey) {
            return currentOrgBudgetKey;
        }
        errorSet.add(`Row ${rowNumber}: Organization and fiscal year are required`);
        return undefined;
    }

    private static findOrCreateOrgBudget(
        budgets: ImportBudget.OrgBudget[],
        orgBudgetKey: string,
        slice: ParsedRowSlice,
        rowNumber: number,
        errorSet: Set<string>,
    ): ImportBudget.OrgBudget | undefined {
        let orgBudget = budgets.find((entry) => this.orgBudgetKey(entry) === orgBudgetKey);
        if (!orgBudget) {
            if (!slice.organizationId && !slice.organizationName?.trim()) {
                errorSet.add(`Row ${rowNumber}: Organization id or name is required`);
                return undefined;
            }
            if (slice.fiscalYearId == null && !slice.fiscalYearName?.trim()) {
                errorSet.add(`Row ${rowNumber}: Fiscal year name is required`);
                return undefined;
            }
            orgBudget = {
                organizationId: slice.organizationId,
                organizationName: slice.organizationName?.trim(),
                fiscalYearId: slice.fiscalYearId,
                fiscalYear: slice.fiscalYearName?.trim(),
                budgetPlans: [],
            };
            budgets.push(orgBudget);
        } else {
            this.mergeOrgBudgetIdentity(orgBudget, slice, rowNumber, errorSet);
        }

        if (slice.budgetName?.trim()) {
            if (orgBudget.name && orgBudget.name !== slice.budgetName.trim()) {
                errorSet.add(
                    `Row ${rowNumber}: Conflicting org budget names "${orgBudget.name}" and "${slice.budgetName.trim()}" for the same organization and fiscal year`,
                );
            } else {
                orgBudget.name = slice.budgetName.trim();
            }
        } else if (!orgBudget.name?.trim()) {
            errorSet.add(`Row ${rowNumber}: Org budget name is required`);
        }

        if (slice.totalAllocated != null) {
            orgBudget.totalAllocated = slice.totalAllocated;
        }
        if (slice.orgBudgetDescription != null) {
            orgBudget.description = slice.orgBudgetDescription;
        }

        return orgBudget;
    }

    private static mergeOrgBudgetIdentity(
        orgBudget: ImportBudget.OrgBudget,
        slice: ParsedRowSlice,
        rowNumber: number,
        errorSet: Set<string>,
    ): void {
        if (
            slice.organizationId != null &&
            orgBudget.organizationId != null &&
            orgBudget.organizationId !== slice.organizationId
        ) {
            errorSet.add(`Row ${rowNumber}: Conflicting organization ids for the same org budget group`);
        } else if (slice.organizationId != null) {
            orgBudget.organizationId = slice.organizationId;
        }

        if (slice.organizationName?.trim()) {
            const incoming = slice.organizationName.trim();
            if (orgBudget.organizationName && orgBudget.organizationName !== incoming) {
                errorSet.add(`Row ${rowNumber}: Conflicting organization names for the same org budget group`);
            } else {
                orgBudget.organizationName = incoming;
            }
        }

        if (
            slice.fiscalYearId != null &&
            orgBudget.fiscalYearId != null &&
            orgBudget.fiscalYearId !== slice.fiscalYearId
        ) {
            errorSet.add(`Row ${rowNumber}: Conflicting fiscal year ids for the same org budget group`);
        } else if (slice.fiscalYearId != null) {
            orgBudget.fiscalYearId = slice.fiscalYearId;
        }

        if (slice.fiscalYearName?.trim()) {
            const incoming = slice.fiscalYearName.trim();
            if (orgBudget.fiscalYear && orgBudget.fiscalYear !== incoming) {
                errorSet.add(`Row ${rowNumber}: Conflicting fiscal year names for the same org budget group`);
            } else {
                orgBudget.fiscalYear = incoming;
            }
        }
    }

    private static findOrCreatePlan(
        orgBudget: ImportBudget.OrgBudget,
        slice: ParsedRowSlice,
    ): ImportBudget.Plan | undefined {
        const planName = slice.planName?.trim();
        if (!planName) {
            return undefined;
        }
        if (!orgBudget.budgetPlans) {
            orgBudget.budgetPlans = [];
        }

        let plan = orgBudget.budgetPlans.find((entry) => entry.name?.trim().toLowerCase() === planName.toLowerCase());
        if (!plan) {
            plan = { name: planName, budgetLineItems: [] };
            orgBudget.budgetPlans.push(plan);
        }

        if (slice.planType?.trim()) {
            plan.planType = slice.planType.trim();
        }
        if (slice.planDescription != null) {
            plan.description = slice.planDescription;
        }
        if (slice.planAllocatedAmount != null) {
            plan.allocatedAmount = slice.planAllocatedAmount;
        }
        if (slice.planStartDate) {
            plan.startDate = slice.planStartDate;
        }
        if (slice.planEndDate) {
            plan.endDate = slice.planEndDate;
        }

        return plan;
    }

    private static parsePlanDateCell(
        rawText: string,
        rowNumber: number,
        errorSet: Set<string>,
        fieldLabel: string,
    ): string | undefined {
        const parsed = S25Util.date.parse(rawText);
        if (!parsed || !S25Util.date.isValid(parsed)) {
            errorSet.add(`Row ${rowNumber}: Invalid ${fieldLabel} "${rawText}"`);
            return undefined;
        }
        return S25Util.date.toS25ISODateTimeStr(parsed) ?? undefined;
    }

    private static addLineItem(
        plan: ImportBudget.Plan,
        slice: ParsedRowSlice,
        planKey: string,
        seenLineKeys: Set<string>,
        rowNumber: number,
        errorSet: Set<string>,
    ): void {
        const lineName = slice.lineItemName?.trim();
        if (!lineName) {
            errorSet.add(`Row ${rowNumber}: Line item name is required`);
            return;
        }
        const categoryKey =
            slice.lineCategoryId != null
                ? `id:${slice.lineCategoryId}`
                : slice.lineCategoryName?.trim()
                  ? `name:${slice.lineCategoryName.trim().toLowerCase()}`
                  : "none";
        const lineKey = `${planKey}\0${lineName.toLowerCase()}\0${categoryKey}`;
        if (seenLineKeys.has(lineKey)) {
            errorSet.add(
                `Row ${rowNumber}: Duplicate line item "${lineName}" with the same category in budget plan "${plan.name}"`,
            );
            return;
        }
        seenLineKeys.add(lineKey);

        if (!plan.budgetLineItems) {
            plan.budgetLineItems = [];
        }

        const lineItem: ImportBudget.LineItem = {
            name: lineName,
            categoryId: slice.lineCategoryId,
            categoryName: slice.lineCategoryName?.trim(),
            plannedAmount: slice.linePlannedAmount,
            description: slice.lineItemDescription,
            comments: slice.lineItemComments,
        };
        plan.budgetLineItems.push(lineItem);
    }

    private static orgBudgetKey(orgBudget: ImportBudget.OrgBudget): string {
        const orgPart =
            orgBudget.organizationId != null
                ? `id:${orgBudget.organizationId}`
                : orgBudget.organizationName
                  ? `name:${orgBudget.organizationName.trim().toLowerCase()}`
                  : "missing-org";
        const fyPart =
            orgBudget.fiscalYearId != null
                ? `fy:${orgBudget.fiscalYearId}`
                : orgBudget.fiscalYear
                  ? `fyname:${orgBudget.fiscalYear.trim().toLowerCase()}`
                  : "missing-fy";
        return `${orgPart}|${fyPart}`;
    }

    private static validateRequiredColumns(
        cellNumToColumn: Map<number, BudgetImportColumn>,
        errorSet: Set<string>,
    ): void {
        const columns = new Set(cellNumToColumn.values());
        const hasOrg = columns.has("organizationId") || columns.has("organizationName");
        if (!hasOrg) {
            errorSet.add("Missing required column: Organization (or Organization Id)");
        }
        if (!columns.has("fiscalYearName")) {
            errorSet.add("Missing required column: Fiscal Year");
        }
    }

    private static validateOrgBudgets(budgets: ImportBudget.OrgBudget[], errorSet: Set<string>): void {
        const planNamesByOrgBudget = new Map<string, Set<string>>();
        for (const orgBudget of budgets) {
            const key = this.orgBudgetKey(orgBudget);
            if (!orgBudget.name?.trim()) {
                errorSet.add(`Org budget for ${key} is missing a budget name`);
            }
            for (const plan of orgBudget.budgetPlans ?? []) {
                const planName = plan.name?.trim().toLowerCase();
                if (!planName) {
                    continue;
                }
                let planSet = planNamesByOrgBudget.get(key);
                if (!planSet) {
                    planSet = new Set<string>();
                    planNamesByOrgBudget.set(key, planSet);
                }
                if (planSet.has(planName)) {
                    errorSet.add(`Duplicate budget plan name "${plan.name}" within org budget ${key}`);
                } else {
                    planSet.add(planName);
                }
            }
        }
    }
}
